<script>
    import { onMount } from 'svelte';
    import EpisodeList from './EpisodeList.svelte';
    import EpisodeDetail from './EpisodeDetail.svelte';
    import AudioPlayerBar from './AudioPlayerBar.svelte';
    import { transformEpisodeData } from './utils/episodeParser.js';

    const checkPurchasedContentUrl =
        'https://yb4fiumrkclurulwgku4arh4dy0jmpjv.lambda-url.eu-central-1.on.aws/digital-purchases';

    let streamingUrl;
    let pdfUrl;
    let content = [];
    let loading = true;
    let error = null;
    let noDigitalContent = false;
    let customerId = '';
    let shop = '';

    // Audio player state
    let currentAudio = null;
    let currentAudioTitle = '';

    // Routing state
    let currentView = 'list'; // 'list' or 'detail'
    let selectedEpisode = null;
    let selectedEpisodeIndex = null;

    // Extract data attributes when component mounts
    $: if (typeof document !== 'undefined') {
        const svelteAppElement = document.getElementById('svelte-app');
        if (svelteAppElement) {
            customerId = svelteAppElement.dataset.customerId || '';
            shop = svelteAppElement.dataset.shop || '';
        }
    }

    // Fetch data from the Lambda function
    const fetchContent = async () => {
        try {
            loading = true;
            error = null;

            if (!customerId || !shop) {
                throw new Error('Customer ID and shop are required');
            }

            const purchasedContent = await fetch(`${checkPurchasedContentUrl}?customerId=${customerId}&shop=${shop}`);
            const purchasedContentJson = await purchasedContent.json();

            if (!purchasedContentJson) {
                throw new Error('Invalid response from Lambda function');
            }

            if (purchasedContentJson.purchases.length === 0) {
                noDigitalContent = true;
                return;
            }

            const contentUrl = purchasedContentJson.contentUrl;
            const response = await fetch(contentUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const rawContent = await response.json();
            pdfUrl = rawContent.urls.downloadFileFnUrl;
            streamingUrl = rawContent.urls.streamFileFnUrl;

            // Transform the data to parse sub-episodes
            content = transformEpisodeData(rawContent);
        } catch (err) {
            error = err.message;
            console.error('Error fetching content:', err);
        } finally {
            loading = false;
        }
    };

    // Fetch content when we have customerId and shop
    $: if (customerId && shop && checkPurchasedContentUrl) {
        fetchContent();
    }

    // Navigation functions
    function navigateToEpisode(index) {
        window.location.hash = `episode-${index + 1}`;
    }

    function navigateBack() {
        window.location.hash = '';
    }

    function handleHashChange() {
        const hash = window.location.hash.slice(1); // Remove #

        if (!hash) {
            currentView = 'list';
            selectedEpisode = null;
            selectedEpisodeIndex = null;
        } else if (hash.startsWith('episode-')) {
            const episodeIndex = parseInt(hash.split('-')[1]) - 1;
            if (content.episodes && content.episodes[episodeIndex]) {
                currentView = 'detail';
                selectedEpisode = content.episodes[episodeIndex];
                selectedEpisodeIndex = episodeIndex;
            } else {
                // Invalid episode, fall back to list
                window.location.hash = '';
            }
        }
    }

    const playAudio = async (filename, chapterName) => {
        try {
            currentAudioTitle = filename.split('.')[0];
            const audioUrl = `${streamingUrl}?key=${chapterName}/${filename}`;
            currentAudio = audioUrl;

            // Force audio element to reload
            const audioElement = document.getElementById('mainPlayer');
            audioElement.load();

            // Add event listeners to debug audio loading
            audioElement.addEventListener('loadstart', () => console.log('Audio loadstart'));
            audioElement.addEventListener('canplay', () => console.log('Audio canplay'));
            audioElement.addEventListener('error', (e) => {
                console.error('Audio error:', e);
                console.error('Audio error details:', audioElement.error);
            });
            audioElement.addEventListener('seeking', () => {
                console.log('Audio seeking');
                audioElement.play().catch((error) => {
                    console.error('Error playing audio after seek:', error);
                });
            });

            // Small delay to ensure the audio element is updated
            setTimeout(() => {
                audioElement.play().catch((error) => {
                    console.error('Error playing audio:', error);
                });
            }, 100);
        } catch (error) {
            console.error('Error in playAudio:', error);
            alert(`Error loading audio file: ${error.message}`);
        }
    };

    onMount(() => {
        window.addEventListener('hashchange', handleHashChange);
        handleHashChange(); // Initialize on load

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    });
</script>

<main>
    {#if loading}
        <div class="loading">Loading content...</div>
    {:else if error}
        <div class="error">
            <p>Error loading content: {error}</p>
            <button on:click={fetchContent}>Retry</button>
        </div>
    {:else if noDigitalContent}
        <div class="error">
            <p>You do not have access to any digital content</p>
        </div>
    {:else if !customerId}
        <div class="error">
            <p>Please login to your account to access your digital content</p>
        </div>
    {:else}
        {#if content.banner && currentView === 'list'}
            <div class="banner">
                <img src={content.banner} alt="Banner" />
            </div>
        {/if}

        {#if currentView === 'list'}
            <EpisodeList episodes={content.episodes} onEpisodeClick={navigateToEpisode} />
        {:else if currentView === 'detail' && selectedEpisode}
            <EpisodeDetail
                episode={selectedEpisode}
                episodeName={selectedEpisode.name}
                onBack={navigateBack}
                {playAudio}
                {pdfUrl}
            />
        {/if}
    {/if}
</main>

<AudioPlayerBar {currentAudio} {currentAudioTitle} />

<style>
    main {
        text-align: center;
        max-width: 1400px;
        margin: 0 auto;
        margin-bottom: 80px;
    }

    .banner {
        margin: 0 auto 2rem;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        max-width: 800px;
        padding: 0 1rem;
    }

    .banner img {
        width: 100%;
        height: auto;
        display: block;
        border-radius: 8px;
    }

    .loading {
        text-align: center;
        padding: 2rem;
        font-size: 1.2rem;
        color: #666;
    }

    .error {
        text-align: center;
        padding: 2rem;
        color: #d32f2f;
    }

    .error button {
        background: #d32f2f;
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
        margin-top: 1rem;
    }

    .error button:hover {
        background: #b71c1c;
    }
</style>
